import { useQuery } from '@apollo/client';
import { useFeatureFlag } from '@newfront-insurance/admin-ui';
import { isEmpty } from '@newfront-insurance/core';
import { useMemo } from 'react';

import { useSkillsSafe } from '../components/chat-v2/contexts/skills-context';
import { buildPromptCommands } from '../lib/slash-command-constants';
import { GET_SKILLS, GET_SLASH_COMMANDS } from '../queries/chats';

interface UsePromptCommandsOptions {
  accountUuid?: string;
  userUuid?: string;
  includeSlashCommands?: boolean;
  includeSkills?: boolean;
}

export function usePromptCommands({
  accountUuid,
  userUuid,
  includeSlashCommands = true,
  includeSkills = true,
}: UsePromptCommandsOptions) {
  const isNovaSkillsEnabled = useFeatureFlag('nova-skills', false);
  const skillsContext = useSkillsSafe();
  const slashCommandsQuery = useQuery(GET_SLASH_COMMANDS, {
    variables: {
      accountUuid,
      userUuid: userUuid ?? '',
    },
    skip: !includeSlashCommands || isEmpty(userUuid),
    fetchPolicy: 'cache-and-network',
  });

  const skillsQuery = useQuery(GET_SKILLS, {
    skip: !includeSkills || !isNovaSkillsEnabled || Boolean(skillsContext) || isEmpty(userUuid),
    fetchPolicy: 'cache-and-network',
  });

  const slashCommands = useMemo(
    () => (includeSlashCommands ? (slashCommandsQuery.data?.slashCommands ?? []) : []),
    [includeSlashCommands, slashCommandsQuery.data?.slashCommands],
  );
  const skills = useMemo(
    () => (includeSkills && isNovaSkillsEnabled ? (skillsContext?.skills ?? skillsQuery.data?.skills ?? []) : []),
    [includeSkills, isNovaSkillsEnabled, skillsContext, skillsQuery.data?.skills],
  );
  const commands = useMemo(() => buildPromptCommands(slashCommands, skills), [skills, slashCommands]);
  const isSkillsLoading =
    includeSkills && isNovaSkillsEnabled ? skillsContext?.isLoading || skillsQuery.loading : false;
  const skillsError = includeSkills && isNovaSkillsEnabled ? (skillsContext?.error ?? skillsQuery.error) : undefined;

  return {
    commands,
    slashCommands,
    skills,
    isLoading: (includeSlashCommands ? slashCommandsQuery.loading : false) || isSkillsLoading,
    error: (includeSlashCommands ? slashCommandsQuery.error : undefined) ?? skillsError,
  };
}
